/**
 * Composable for handling video player JavaScript errors
 * Handles RangeError and InvalidStateError by triggering retry mechanism
 * Automatically registers and unregisters error handler on mount/unmount
 */
export const useVideoPlayerErrorHandler = (
    dispose: (callback?: () => void) => Promise<void>,
    onRetry: () => void
) => {
    const handleJSErrors = (event: ErrorEvent) => {
        const err = event.error;
        if (!err) {
            return;
        }

        // Check for RangeError with Array buffer allocation failed first
        if ((err instanceof RangeError || err.name === 'RangeError') &&
            err.message?.includes('Array buffer allocation failed')) {
            event.preventDefault();
            console.warn('Preventing default behavior for RangeError (Array buffer allocation failed) and handling it with a retry mechanism; Error:', err);
            void dispose(() => {
                onRetry();
            });
            return;
        }

        if (err instanceof DOMException &&
            err.name === 'InvalidStateError' &&
            err.message?.includes('responseText')
        ) {
            event.preventDefault();
            console.warn('Preventing default behavior for InvalidStateError and handling it with a retry mechanism; Error:', err);
            void dispose(() => {
                onRetry();
            });
        }
    };

    onMounted(() => {
        window.addEventListener('error', handleJSErrors);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('error', handleJSErrors);
    });

    return {
        handleJSErrors
    };
};
